import React, {useState} from 'react'
import {motion, AnimatePresence} from 'framer-motion'
import {useFamily} from '../../contexts/FamilyContext'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import Button from '../ui/Button'
import toast from 'react-hot-toast'

const {FiX, FiSave} = FiIcons

const EditRewardModal = ({isOpen, onClose, reward}) => {
  const {updateReward} = useFamily()
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    title: reward?.title || '',
    description: reward?.description || '',
    points_required: reward?.points_required || 50,
    emoji: reward?.emoji || '🎁'
  })

  // Reset form data when reward changes
  React.useEffect(() => {
    if (reward) {
      setFormData({
        title: reward.title || '',
        description: reward.description || '',
        points_required: reward.points_required || 50,
        emoji: reward.emoji || '🎁'
      })
    }
  }, [reward])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!reward) return

    setLoading(true)

    try {
      const {data, error} = await updateReward(reward.id, {
        title: formData.title.trim(),
        description: formData.description.trim() || null,
        points_required: parseInt(formData.points_required),
        emoji: formData.emoji || '🎁'
      })

      if (!error && data) {
        onClose()
      }
    } catch (error) {
      console.error('Error updating reward:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    const {name, value} = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const emojiOptions = ['🎁', '🏆', '⭐', '🎉', '🍦', '🎮', '📱', '🎬', '🍕', '🧸', '🎈', '💎', '🌟', '🎪', '🎯']

  if (!reward) return null

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{opacity: 0, scale: 0.95, y: 20}}
            animate={{opacity: 1, scale: 1, y: 0}}
            exit={{opacity: 0, scale: 0.95, y: 20}}
            className="relative bg-white rounded-2xl shadow-xl w-full max-w-md"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Edit Reward</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  className="p-2"
                  disabled={loading}
                >
                  <SafeIcon icon={FiX} />
                </Button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reward Title *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="e.g., Extra Screen Time"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Optional details about the reward..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Points Required *
                    </label>
                    <input
                      type="number"
                      name="points_required"
                      value={formData.points_required}
                      onChange={handleChange}
                      min="1"
                      max="1000"
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Emoji
                    </label>
                    <div className="grid grid-cols-5 gap-1 border border-gray-300 rounded-lg p-2 max-h-20 overflow-y-auto">
                      {emojiOptions.map((emoji, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => setFormData(prev => ({...prev, emoji}))}
                          className={`p-1 rounded text-lg hover:bg-gray-100 ${
                            formData.emoji === emoji ? 'bg-primary-100' : ''
                          }`}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={onClose}
                    className="flex-1"
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    className="flex-1"
                    loading={loading}
                    disabled={!formData.title.trim() || !formData.points_required}
                  >
                    <SafeIcon icon={FiSave} className="text-sm" />
                    Save Changes
                  </Button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}

export default EditRewardModal